import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import { getImageUrl } from '../utils/imageUtils';

function RecipeOverview() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('tiles'); // 'tiles' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [filterDuration, setFilterDuration] = useState('');
  const [filterMealType, setFilterMealType] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`/api/recipes`);
      setRecipes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) {
      return;
    }
    try {
      await axios.delete(`/api/recipes/${id}`);
      fetchRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading recipes...</div>;
  }

  return (
    <>
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/form-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">All Recipes</h1>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* View Toggle */}
            <div className="flex bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('tiles')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  viewMode === 'tiles'
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900 bg-gray-100'
                }`}
                title="Tile View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  viewMode === 'list'
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900 bg-gray-100'
                }`}
                title="List View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
            <button
              onClick={() => navigate('/recipes/new')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-amber-700 transition-colors shadow-md whitespace-nowrap"
            >
              Add Recipe
            </button>
          </div>
        </div>

        <div className="mb-6 space-y-4">
          <input
            type="text"
            placeholder="Search recipes by name, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-sm"
          />
          
          {/* Filters */}
          <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-md p-4">
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="w-full flex items-center justify-between gap-2 mb-3 hover:bg-gray-50/50 rounded-lg p-2 -m-2 transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                {(filterRating || filterDuration || filterTags.length > 0 || filterMealType) && (
                  <span className="px-2 py-0.5 bg-amber-600 text-white text-xs font-semibold rounded-full">
                    {(filterRating ? 1 : 0) + (filterDuration ? 1 : 0) + filterTags.length + (filterMealType ? 1 : 0)} active
                  </span>
                )}
              </div>
              <svg
                className={`w-5 h-5 text-gray-600 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {filtersExpanded && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Rating
                  </label>
                  <select
                    value={filterRating}
                    onChange={(e) => setFilterRating(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All Ratings</option>
                    <option value="5">5 stars</option>
                    <option value="4">4+ stars</option>
                    <option value="3">3+ stars</option>
                    <option value="2">2+ stars</option>
                    <option value="1">1+ stars</option>
                  </select>
                </div>

                {/* Duration Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration
                  </label>
                  <select
                    value={filterDuration}
                    onChange={(e) => setFilterDuration(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All Durations</option>
                    <option value="15">15 min or less</option>
                    <option value="30">30 min or less</option>
                    <option value="30-60">Between 30 min – 1 hour</option>
                    <option value="60-120">Between 1 – 2 hours</option>
                    <option value="121">More than 2 hours</option>
                  </select>
                </div>

                {/* Meal Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meal Type
                  </label>
                  <select
                    value={filterMealType}
                    onChange={(e) => setFilterMealType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All Meal Types</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>
              </div>

              {/* Tags Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="border border-gray-300 rounded-lg p-2 bg-white">
                  {(() => {
                    const allTags = [...new Set(recipes.flatMap(r => r.tags || []))].sort();
                    if (allTags.length === 0) {
                      return <p className="text-sm text-gray-500">No tags available</p>;
                    }
                    return (
                      <div className="flex flex-wrap gap-2">
                        {allTags.map((tag) => (
                          <label key={tag} className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={filterTags.includes(tag)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilterTags([...filterTags, tag]);
                                } else {
                                  setFilterTags(filterTags.filter(t => t !== tag));
                                }
                              }}
                              className="rounded text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-sm text-gray-700">{tag}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {filterTags.length > 0 && (
                  <button
                    onClick={() => setFilterTags([])}
                    className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Clear tags
                  </button>
                )}
              </div>
              {(filterRating || filterDuration || filterTags.length > 0 || filterMealType) && (
                <button
                  onClick={() => {
                    setFilterRating('');
                    setFilterDuration('');
                    setFilterTags([]);
                    setFilterMealType('');
                  }}
                  className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors text-sm"
                >
                  Clear All Filters
                </button>
              )}
            </div>
            )}
          </div>
        </div>

        {(() => {
          const filteredRecipes = recipes.filter(recipe => {
            // Search filter
            if (searchTerm) {
              const searchLower = searchTerm.toLowerCase();
              const matchesSearch = (
                recipe.title?.toLowerCase().includes(searchLower) ||
                recipe.description?.toLowerCase().includes(searchLower) ||
                recipe.tags?.some(tag => tag.toLowerCase().includes(searchLower))
              );
              if (!matchesSearch) return false;
            }

            // Rating filter
            if (filterRating) {
              const minRating = parseInt(filterRating);
              if (!recipe.rating || recipe.rating < minRating) {
                return false;
              }
            }

            // Duration filter
            if (filterDuration) {
              const duration = recipe.totalTime || (recipe.prepTime && recipe.cookTime ? recipe.prepTime + recipe.cookTime : null);
              if (!duration) return false;

              if (filterDuration === '30-60') {
                if (duration <= 30 || duration > 60) return false;
              } else if (filterDuration === '60-120') {
                if (duration <= 60 || duration > 120) return false;
              } else if (filterDuration === '121') {
                if (duration <= 120) return false;
              } else {
                const maxDuration = parseInt(filterDuration, 10);
                if (duration > maxDuration) return false;
              }
            }

            // Tags filter - all selected tags must be present (AND logic)
            if (filterTags.length > 0) {
              const recipeTags = recipe.tags || [];
              const hasAllTags = filterTags.every(tag => recipeTags.includes(tag));
              if (!hasAllTags) return false;
            }

            // Meal type filter
            if (filterMealType) {
              const recipeMealTypes = recipe.suitableMealTypes || [];
              if (!recipeMealTypes.includes(filterMealType)) {
                return false;
              }
            }

            return true;
          });

          return filteredRecipes.length === 0 ? (
          <div className="text-center py-16 bg-white/80 rounded-lg">
              <p className="text-xl text-gray-600 mb-4">
                {recipes.length === 0 
                  ? "No recipes yet!" 
                  : "No recipes found matching your filters"}
              </p>
              {recipes.length === 0 && (
            <button
              onClick={() => navigate('/recipes/new')}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md"
            >
              Create Your First Recipe
            </button>
              )}
          </div>
        ) : viewMode === 'tiles' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRecipes.map((recipe) => (
              <div
                key={recipe._id}
                className="bg-white/60 backdrop-blur-sm rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer flex flex-col h-full"
                onClick={() => navigate(`/recipes/${recipe._id}`)}
              >
                {recipe.imageUrl ? (
                  <img
                    src={getImageUrl(recipe.imageUrl)}
                    alt={recipe.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                    <svg 
                      className="w-20 h-20 text-orange-400" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                  </div>
                )}
                <div className="p-4 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-xl font-semibold">{recipe.title}</h2>
                      {recipe.rating && (
                        <div className="flex items-center gap-1">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <span
                                key={i}
                                className={`text-sm ${
                                  i < recipe.rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-600">
                            {recipe.rating}/5
                          </span>
                        </div>
                      )}
                    </div>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {recipe.description}
                  </p>
                  <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                    <span>{recipe.totalTime || (recipe.prepTime && recipe.cookTime ? recipe.prepTime + recipe.cookTime : 'N/A')} min</span>
                    <span>{recipe.servings} servings</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-auto">
                    {recipe.tags && recipe.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 flex-1">
                        {recipe.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/recipes/${recipe._id}`);
                        }}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors text-sm shadow-md"
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(recipe._id);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm shadow-md"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
              {filteredRecipes.map((recipe) => (
              <div
                key={recipe._id}
                className="bg-white/60 backdrop-blur-sm rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer flex flex-col sm:flex-row sm:items-stretch"
                onClick={() => navigate(`/recipes/${recipe._id}`)}
              >
                <div className="w-full sm:w-48 h-48 flex-shrink-0">
                  {recipe.imageUrl ? (
                    <img
                      src={getImageUrl(recipe.imageUrl)}
                      alt={recipe.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                      <svg 
                        className="w-20 h-20 text-orange-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-6 flex-1 flex flex-col min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h2 className="text-xl sm:text-2xl font-semibold line-clamp-1">{recipe.title}</h2>
                      {recipe.rating && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <span
                                key={i}
                                className={`text-sm ${
                                  i < recipe.rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-600">
                            {recipe.rating}/5
                          </span>
                        </div>
                      )}
                    </div>
                  <p className="text-gray-600 mb-3 line-clamp-2">
                    {recipe.description}
                  </p>
                  <div className="flex gap-4 items-center text-sm text-gray-500 mb-3">
                    <span>{recipe.totalTime || (recipe.prepTime && recipe.cookTime ? recipe.prepTime + recipe.cookTime : 'N/A')} min</span>
                    <span>•</span>
                    <span>{recipe.servings} servings</span>
                  </div>
                  {recipe.tags && recipe.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {recipe.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className="flex gap-2 px-4 pt-4 pb-5 sm:px-6 sm:pb-6 sm:pt-4 flex-shrink-0 items-center justify-end sm:self-stretch sm:items-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => navigate(`/recipes/${recipe._id}`)}
                    className="px-6 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md whitespace-nowrap"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDelete(recipe._id)}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md whitespace-nowrap"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>
    </div>
    </>
  );
}

export default RecipeOverview;